import React from 'react'

interface CredentialConfirmCardProps {
  appName: string
  credentialName: string
  logoUrl: string
  credentialType: string
  expiresAt: string | null
  status: 'active'
  onConfirm: () => void
  onUseDifferent: () => void
  locked: boolean
}

/**
 * CredentialConfirmCard — displays an org credential for user confirmation
 * during the AI dashboard creation flow (Step 3a).
 *
 * Shows: app logo tile, credential name, credential type, expiration date (if present),
 * Active badge, and two action buttons. Both buttons disable when locked.
 */
export const CredentialConfirmCard: React.FC<CredentialConfirmCardProps> = ({
  appName,
  credentialName,
  logoUrl,
  credentialType,
  expiresAt,
  onConfirm,
  onUseDifferent,
  locked,
}) => {
  const expiresFormatted = expiresAt
    ? new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null

  const buttonLocked = `opacity-65 cursor-default pointer-events-none`
  const secondaryLocked = `opacity-60 cursor-default pointer-events-none`

  return (
    <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-[10px] p-[14px] mb-[14px]">
      {/* Header row: logo + name/type + Active badge */}
      <div className="flex items-start gap-3">
        {/* App logo tile */}
        <div className="w-9 h-9 rounded-[8px] bg-[#eff6ff] flex items-center justify-center flex-shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={`${appName} logo`} className="w-4 h-4 object-contain" />
          ) : (
            <div
              aria-hidden="true"
              className="w-4 h-4 flex items-center justify-center text-accent font-semibold text-[11px]"
            >
              {appName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Credential info */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-primary leading-tight">{credentialName}</p>
          <p className="text-[12px] text-secondary mt-[2px]">{credentialType}</p>
          {expiresFormatted && (
            <p className="text-[12px] text-secondary mt-[2px]">Expires {expiresFormatted}</p>
          )}
        </div>

        {/* Active badge */}
        <span className="inline-flex items-center gap-[5px] text-[12px] font-semibold px-[10px] py-[3px] rounded-full bg-[#f0fdf4] text-[#16a34a] flex-shrink-0 whitespace-nowrap">
          <svg width="7" height="7" viewBox="0 0 7 7" fill="currentColor" aria-hidden="true">
            <circle cx="3.5" cy="3.5" r="3.5" />
          </svg>
          Active
        </span>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-[14px]">
        <button
          type="button"
          aria-label="Yes, use this credential"
          className={`w-full bg-accent text-white rounded-[8px] py-[10px] text-[13px] font-semibold ${locked ? buttonLocked : ''}`}
          onClick={onConfirm}
          disabled={locked}
        >
          Yes, use this credential
        </button>
        <button
          type="button"
          aria-label={`Use a different ${appName} credential`}
          className={`w-full bg-card text-primary border border-[#e2e8f0] rounded-[8px] py-[10px] text-[13px] font-medium ${locked ? secondaryLocked : ''}`}
          onClick={onUseDifferent}
          disabled={locked}
        >
          Use a different one
        </button>
      </div>
    </div>
  )
}
